import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Modal from "@mui/material/Modal";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import api from "services/api";
import { useTranslation } from "react-i18next";
import { formatDate } from "utils/formatDate";
import { libelleValeur } from "utils/valeurs";
import { useAuth } from "../../context/AuthContext";
import AppelModal from "./AppelModal";

const STATUT_CONFIG = {
  en_attente: { bg: "#fff8e1", color: "#f57c00", label: "valeurs.statutPresence.en_attente", icon: "hourglass_empty" },
  confirme:   { bg: "#e8f5e9", color: "#2e7d32", label: "valeurs.statutPresence.confirme",  icon: "check_circle" },
  refuse:     { bg: "#ffebee", color: "#c62828", label: "valeurs.statutPresence.refuse",    icon: "cancel" },
  present:    { bg: "#e3f2fd", color: "#1565c0", label: "valeurs.statutPresence.present",   icon: "person" },
  absent:     { bg: "#fafafa", color: "#616161", label: "valeurs.statutPresence.absent",    icon: "person_off" },
};

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <MDBox display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
      <Icon sx={{ fontSize: 20, color: "#1565c0", mt: 0.5 }}>{icon}</Icon>
      <MDBox>
        <MDTypography variant="caption" sx={{ textTransform: "uppercase", fontSize: 10, color: "#9e9e9e", display: "block" }}>
          {label}
        </MDTypography>
        <MDTypography variant="body2" color="dark" sx={{ whiteSpace: "pre-wrap" }}>{value}</MDTypography>
      </MDBox>
    </MDBox>
  );
}

export default function ReunionDetail() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reunion, setReunion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [appelOpen, setAppelOpen] = useState(false);
  const [compteRendu, setCompteRendu] = useState("");
  const [savingCR, setSavingCR] = useState(false);
  const [crDirty, setCrDirty] = useState(false);
  const [envoiCrOpen, setEnvoiCrOpen] = useState(false);
  const [emailsCr, setEmailsCr] = useState("");
  const [envoyantCr, setEnvoyantCr] = useState(false);
  const [ensSelectionnes, setEnsSelectionnes] = useState({});

  const load = () => {
    api.get(`/reunions/${id}`).then(({ data }) => {
      setReunion(data);
      setCompteRendu(data.compte_rendu || "");
      setCrDirty(false);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const saveCompteRendu = async () => {
    setSavingCR(true);
    try {
      await api.put(`/reunions/${id}`, { compte_rendu: compteRendu });
      await load();
      setFeedback({ type: "success", text: t("reunions.feedback.crEnregistre") });
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.message || t("commun.erreurEnregistrement") });
    }
    setSavingCR(false);
  };

  const envoyerCR = async () => {
    // Combine emails cochés + saisie libre, filtre les doublons
    const emailsCoches = (reunion.convocations || [])
      .filter((c) => ensSelectionnes[c.user_id] && c.enseignant?.email)
      .map((c) => c.enseignant.email);
    const emailsLibres = String(emailsCr).split(/[,;]/).map((e) => e.trim()).filter(Boolean);
    const tousEmails = [...new Set([...emailsCoches, ...emailsLibres])];

    if (tousEmails.length === 0) {
      setFeedback({ type: "error", text: t("reunions.feedback.aucunDestinataire") });
      return;
    }

    setEnvoyantCr(true);
    try {
      const { data } = await api.post(`/reunions/${id}/envoyer-cr`, { emails: tousEmails.join(",") });
      setFeedback({
        type: data.erreurs?.length ? "warning" : "success",
        text: t("reunions.feedback.emailsEnvoyes", { n: data.envoyes, total: data.total }) + (data.erreurs?.length ? t("reunions.feedback.voirDetails") : "."),
        erreurs: data.erreurs || [],
      });
      setEnvoiCrOpen(false);
      setEmailsCr("");
      setEnsSelectionnes({});
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.message || t("email.erreurEnvoi") });
    }
    setEnvoyantCr(false);
  };

  const toggleAllEns = (val) => {
    const next = {};
    (reunion?.convocations || []).forEach((c) => {
      if (c.enseignant?.email) next[c.user_id] = val;
    });
    setEnsSelectionnes(next);
  };

  const rouvrirReunion = async () => {
    const c1 = window.confirm(t("reunions.detail.confirmerRouvrir"));
    if (!c1) return;
    const c2 = window.confirm(t("reunions.detail.confirmerRouvrir2"));
    if (!c2) return;
    try {
      await api.put(`/reunions/${id}`, {
        titre: reunion.titre, date: reunion.date, heure_debut: reunion.heure_debut,
        heure_fin: reunion.heure_fin, lieu: reunion.lieu, ordre_du_jour: reunion.ordre_du_jour,
        statut: "planifiee",
      });
      await load();
      setFeedback({ type: "success", text: t("reunions.feedback.rouverte") });
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.message || t("commun.erreur") });
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleNotifier = async () => {
    setNotifying(true);
    setFeedback(null);
    try {
      const { data } = await api.post(`/reunions/${id}/notifier`);
      setFeedback({
        type: data.erreurs?.length ? "warning" : "success",
        text: t("reunions.feedback.invitationsEnvoyees", { n: data.envoyes, total: data.total }) + (data.erreurs?.length ? t("reunions.feedback.voirDetails") : "."),
        erreurs: data.erreurs || [],
      });
      load();
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.message || t("email.erreurEnvoi") });
    }
    setNotifying(false);
  };

  const handleRappeler = async () => {
    setNotifying(true);
    setFeedback(null);
    try {
      const { data } = await api.post(`/reunions/${id}/rappeler`);
      setFeedback({
        type: data.erreurs?.length ? "warning" : "success",
        text: t("reunions.feedback.rappelsEnvoyes", { n: data.envoyes, total: data.total }) + (data.erreurs?.length ? t("reunions.feedback.voirDetails") : "."),
        erreurs: data.erreurs || [],
      });
      load();
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.message || t("email.erreurEnvoi") });
    }
    setNotifying(false);
  };

  const setPresence = async (user_id, statut_presence) => {
    try {
      await api.put(`/reunions/${id}/presence`, { user_id, statut_presence });
      load();
    } catch (err) {
      alert(err.response?.data?.message || t("commun.erreur"));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("reunions.detail.confirmerSuppression"))) return;
    await api.delete(`/reunions/${id}`);
    navigate("/reunions");
  };

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox p={4} textAlign="center"><MDTypography>{t("commun.chargement")}</MDTypography></MDBox>
    </DashboardLayout>
  );
  if (!reunion) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox p={4} textAlign="center"><MDTypography>{t("reunions.detail.introuvable")}</MDTypography></MDBox>
    </DashboardLayout>
  );

  const convocations = reunion.convocations || [];
  const nbConfirmes = convocations.filter((c) => c.statut_presence === "confirme").length;
  const nbRefuses = convocations.filter((c) => c.statut_presence === "refuse").length;
  const nbEnAttente = convocations.filter((c) => c.statut_presence === "en_attente").length;
  const nbPresents = convocations.filter((c) => c.statut_presence === "present").length;
  const nbAbsents = convocations.filter((c) => c.statut_presence === "absent").length;
  const nbNonPointes = convocations.length - nbPresents - nbAbsents;
  const reunionPasseeOuAujourdhui = new Date(reunion.date) <= new Date(new Date().setHours(23,59,59,999));
  const modeJourJ = reunionPasseeOuAujourdhui && (nbPresents + nbAbsents) > 0;

  const isCloturee = reunion.statut === "terminee";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isCloturee && (
          <MDBox mb={2} p={2} sx={{
            background: "#e8f5e9", borderLeft: "4px solid #2e7d32", borderRadius: "8px",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
          }}>
            <MDBox>
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                🔒 {t("reunions.detail.cloturee")}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {t("reunions.detail.clotureeAide")}
              </MDTypography>
            </MDBox>
            {isAdmin && (
            <MDButton size="small" variant="outlined" color="warning" onClick={rouvrirReunion}>
              <Icon sx={{ mr: 0.5 }}>lock_open</Icon> {t("reunions.detail.rouvrir")}
            </MDButton>
            )}
          </MDBox>
        )}
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <MDButton variant="text" color="secondary" size="small" onClick={() => navigate("/reunions")}>
            <Icon>arrow_back</Icon> {t("commun.retour")}
          </MDButton>
          <MDTypography variant="h4" fontWeight="bold" color="dark" sx={{ flex: 1 }}>
            {reunion.titre}
          </MDTypography>
          {(() => {
            const isPast = new Date(reunion.date) <= new Date(new Date().setHours(23,59,59,999));
            if (!isAdmin) return null;
            return (
              <MDButton
                onClick={() => isAdmin && isPast && !isCloturee && setAppelOpen(true)}
                title={isCloturee ? t("reunions.detail.cloturee") : isPast ? t("reunions.detail.faireAppel") : t("reunions.detail.disponibleJourJ")}
                sx={{
                  background: (isPast && !isCloturee) ? "linear-gradient(135deg, #2e7d32, #43a047)" : "#e0e0e0",
                  color: (isPast && !isCloturee) ? "#fff" : "#757575",
                  fontWeight: 700,
                  cursor: (isPast && !isCloturee) ? "pointer" : "not-allowed",
                  opacity: isPast ? 1 : 0.9,
                  "&:hover": {
                    background: isPast ? "linear-gradient(135deg, #1b5e20, #2e7d32)" : "#e0e0e0",
                  },
                }}>
                <Icon sx={{ mr: 0.5, color: isPast ? "#fff" : "#757575" }}>fact_check</Icon>
                {t("reunions.detail.faireAppel")}
              </MDButton>
            );
          })()}
          {isAdmin && (
            <>
          <MDButton variant="outlined" color="info" size="small" disabled={isCloturee} onClick={() => navigate(`/reunions/${id}/modifier`)}>
            <Icon sx={{ mr: 0.5 }}>edit</Icon> {t("commun.modifier")}
          </MDButton>
          <MDButton variant="outlined" color="error" size="small" onClick={handleDelete}>
            <Icon sx={{ mr: 0.5 }}>close</Icon> {t("commun.supprimer")}
          </MDButton>
            </>
          )}
        </MDBox>

        <Grid container spacing={3}>
          {/* Informations */}
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 3 }}>
              <MDTypography variant="h6" mb={2}>{t("reunions.informations")}</MDTypography>
              <InfoRow icon="event" label={t("champs.date")} value={
                formatDate(reunion.date, undefined, "long")
              } />
              <InfoRow icon="schedule" label={t("champs.horaire")} value={
                `${reunion.heure_debut}${reunion.heure_fin ? " — " + reunion.heure_fin : ""}`
              } />
              <InfoRow icon="place" label={t("champs.lieu")} value={reunion.lieu} />
              <InfoRow icon="assignment" label={t("champs.ordreDuJour")} value={reunion.ordre_du_jour} />
              <InfoRow icon="flag" label={t("champs.statut")} value={libelleValeur("statutReunion", reunion.statut)} />
            </Card>
          </Grid>

          {/* Convocations */}
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDBox>
                  <MDTypography variant="h6">{t("reunions.detail.convocations", { n: convocations.length })}</MDTypography>
                  <MDTypography variant="caption" color="text">
                    {modeJourJ ? (
                      <>
                        ✅ {t("reunions.detail.nbPresents", { count: nbPresents })} · ❌ {t("reunions.detail.nbAbsents", { count: nbAbsents })} · ⏳ {t("reunions.detail.nbNonPointes", { count: nbNonPointes })}
                      </>
                    ) : (
                      <>
                        ✅ {t("reunions.detail.nbConfirmes", { count: nbConfirmes })} · ❌ {t("reunions.detail.nbRefuses", { count: nbRefuses })} · ⏳ {t("reunions.detail.nbEnAttente", { count: nbEnAttente })}
                      </>
                    )}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  {isAdmin && (
                    <>
                  <MDButton onClick={handleNotifier} disabled={notifying || convocations.length === 0 || isCloturee}
                    sx={{ background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 700 }}>
                    <Icon sx={{ mr: 0.5 }}>send</Icon>
                    {notifying ? t("commun.envoi") : t("reunions.detail.envoyerInvitation")}
                  </MDButton>
                  <MDButton onClick={handleRappeler} disabled={notifying || convocations.length === 0 || isCloturee}
                    sx={{ background: "linear-gradient(135deg, #ef6c00, #f57c00)", color: "#fff", fontWeight: 700 }}>
                    <Icon sx={{ mr: 0.5 }}>notifications_active</Icon>
                    {notifying ? t("commun.envoi") : t("reunions.detail.envoyerRappel")}
                  </MDButton>
                    </>
                  )}
                </MDBox>
              </MDBox>

              {feedback && (
                <MDBox p={2} mb={2} sx={{
                  background: feedback.type === "success" ? "#e8f5e9" : feedback.type === "warning" ? "#fff8e1" : "#ffebee",
                  color: feedback.type === "success" ? "#2e7d32" : feedback.type === "warning" ? "#f57c00" : "#c62828",
                  borderRadius: "8px", fontSize: 13,
                }}>
                  {feedback.text}
                  {feedback.erreurs?.length > 0 && (
                    <ul style={{ margin: "8px 0 0 16px", padding: 0, fontSize: 12 }}>
                      {feedback.erreurs.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </MDBox>
              )}

              {convocations.length === 0 ? (
                <MDTypography variant="body2" color="text">{t("reunions.aucunConvoque")}</MDTypography>
              ) : (
                <MDBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {convocations.map((c) => {
                    const cfg = STATUT_CONFIG[c.statut_presence] || STATUT_CONFIG.en_attente;
                    return (
                      <MDBox key={c.user_id} sx={{
                        p: 1.5, borderRadius: "8px", background: "#f8faff",
                        display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
                      }}>
                        <MDBox sx={{ flex: 1, minWidth: 150 }}>
                          <MDTypography variant="body2" fontWeight="bold" color="dark">
                            {c.enseignant?.prenom} {c.enseignant?.nom}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" sx={{ display: "block", fontSize: 11 }}>
                            <bdi dir="ltr">{c.enseignant?.email || `— ${t("email.pasEmail")} —`}</bdi>
                          </MDTypography>
                          {c.notifie_le && (
                            <MDTypography variant="caption" color="text" sx={{ display: "block", fontSize: 10, color: "#9e9e9e" }}>
                              {t("reunions.detail.notifieLe", { date: formatDate(c.notifie_le, undefined, "dateHeure") })}
                            </MDTypography>
                          )}
                        </MDBox>
                        <span style={{
                          background: cfg.bg, color: cfg.color, borderRadius: 12,
                          padding: "4px 10px", fontSize: 11, fontWeight: 700,
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}>
                          <Icon sx={{ fontSize: 14 }}>{cfg.icon}</Icon>
                          {t(cfg.label)}
                        </span>
                        <MDBox display="flex" gap={0.5}>
                          <MDButton size="small" variant="text" color="success"
                            onClick={() => setPresence(c.user_id, "present")}
                            title={t("reunions.detail.marquerPresent")}>
                            <Icon>person</Icon>
                          </MDButton>
                          <MDButton size="small" variant="text" color="error"
                            onClick={() => setPresence(c.user_id, "absent")}
                            title={t("reunions.detail.marquerAbsent")}>
                            <Icon>person_off</Icon>
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    );
                  })}
                </MDBox>
              )}
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <MDBox>
                  <MDTypography variant="h6">📝 {t("reunions.cr.titre")}</MDTypography>
                  <MDTypography variant="caption" color="text">
                    {t("reunions.cr.aide")}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  <MDButton onClick={saveCompteRendu} disabled={savingCR || !crDirty}
                    sx={{ background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 700 }}>
                    <Icon sx={{ mr: 0.5 }}>save</Icon>
                    {savingCR ? t("commun.enregistrement") : crDirty ? t("commun.enregistrer") : t("commun.enregistre")}
                  </MDButton>
                  <MDButton onClick={() => setEnvoiCrOpen(true)}
                    disabled={!reunion.compte_rendu || crDirty}
                    variant="outlined" color="info">
                    <Icon sx={{ mr: 0.5 }}>send</Icon> {t("reunions.cr.envoyerEmail")}
                  </MDButton>
                </MDBox>
              </MDBox>
              <TextField fullWidth multiline rows={8}
                placeholder={t("reunions.cr.placeholder")}
                value={compteRendu}
                onChange={(e) => { setCompteRendu(e.target.value); setCrDirty(true); }} />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modale envoi compte-rendu par email */}
      <Modal open={envoiCrOpen} onClose={() => setEnvoiCrOpen(false)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <MDBox sx={{ background: "#fff", borderRadius: "12px", width: "100%", maxWidth: 560, maxHeight: "92vh", display: "flex", flexDirection: "column", outline: "none" }}>
          <MDBox p={3} pb={1}>
            <MDTypography variant="h6">📧 {t("reunions.cr.envoyerTitre")}</MDTypography>
            <MDTypography variant="caption" color="text">
              {t("reunions.cr.envoyerAide")}
            </MDTypography>
          </MDBox>

          <MDBox sx={{ overflowY: "auto", px: 3, flex: 1 }}>
            {/* Bloc enseignants convoqués */}
            {(reunion.convocations || []).length > 0 && (
              <MDBox mb={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="caption" fontWeight="bold" sx={{
                    textTransform: "uppercase", fontSize: 11, letterSpacing: "0.05em", color: "#9e9e9e",
                  }}>
                    {t("reunions.cr.enseignantsConvoques", { count: Object.values(ensSelectionnes).filter(Boolean).length })}
                  </MDTypography>
                  <MDBox display="flex" gap={0.5}>
                    <MDButton size="small" variant="text" color="info" onClick={() => toggleAllEns(true)}>{t("commun.toutCocher")}</MDButton>
                    <MDButton size="small" variant="text" color="secondary" onClick={() => toggleAllEns(false)}>{t("commun.toutDecocher")}</MDButton>
                  </MDBox>
                </MDBox>
                <MDBox sx={{
                  maxHeight: 240, overflowY: "auto",
                  border: "1px solid #e0e0e0", borderRadius: "8px", background: "#fff", p: 1,
                }}>
                  {(reunion.convocations || []).map((c) => {
                    const noEmail = !c.enseignant?.email;
                    return (
                      <label key={c.user_id} style={{
                        display: "flex", alignItems: "center", gap: 10, marginBottom: 4,
                        cursor: noEmail ? "not-allowed" : "pointer", fontSize: 13, color: "#344767",
                        padding: "6px 8px", borderRadius: "6px",
                        background: ensSelectionnes[c.user_id] ? "#e3f2fd" : "transparent",
                        opacity: noEmail ? 0.5 : 1,
                      }}>
                        <input type="checkbox"
                          disabled={noEmail}
                          checked={!!ensSelectionnes[c.user_id]}
                          onChange={(ev) => setEnsSelectionnes((prev) => ({ ...prev, [c.user_id]: ev.target.checked }))}
                          style={{ cursor: noEmail ? "not-allowed" : "pointer", width: 16, height: 16 }} />
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                          <strong>{c.enseignant?.prenom} {c.enseignant?.nom}</strong>
                          {c.enseignant?.email && <span style={{ color: "#9e9e9e", marginLeft: 6, fontSize: 11 }}>— <bdi dir="ltr">{c.enseignant.email}</bdi></span>}
                        </span>
                        {noEmail && (
                          <span style={{ background: "#ffebee", color: "#c62828", fontSize: 10, padding: "2px 6px", borderRadius: 4 }}>
                            {t("commun.sansEmail")}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </MDBox>
              </MDBox>
            )}

            {/* Bloc adresses libres */}
            <MDBox mb={1}>
              <MDTypography variant="caption" fontWeight="bold" sx={{
                textTransform: "uppercase", fontSize: 11, letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 1,
              }}>
                {t("reunions.cr.adressesSupp")}
              </MDTypography>
              <TextField fullWidth multiline rows={2}
                placeholder="president@apbgest.fr, contact@..."
                value={emailsCr}
                onChange={(e) => setEmailsCr(e.target.value)} />
              <MDTypography variant="caption" color="text" sx={{ fontSize: 10, mt: 0.5, display: "block" }}>
                {t("reunions.cr.adressesAide")}
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Footer */}
          <MDBox p={2} sx={{ borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <MDTypography variant="caption" color="text">
              {t("reunions.cr.total", { n: Object.values(ensSelectionnes).filter(Boolean).length + String(emailsCr).split(/[,;]/).filter((e) => e.trim()).length })}
            </MDTypography>
            <MDBox display="flex" gap={1}>
              <MDButton variant="outlined" color="secondary" onClick={() => setEnvoiCrOpen(false)}>{t("commun.annuler")}</MDButton>
              <MDButton onClick={envoyerCR}
                disabled={envoyantCr || (Object.values(ensSelectionnes).filter(Boolean).length === 0 && !emailsCr.trim())}
                sx={{ background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 700 }}>
                <Icon sx={{ mr: 0.5 }}>send</Icon>
                {envoyantCr ? t("commun.envoi") : t("email.envoyer")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Modal>

      <AppelModal open={appelOpen} onClose={() => setAppelOpen(false)} reunion={reunion} onUpdated={load} />
    </DashboardLayout>
  );
}
